"use client";

import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFullName, getStudentProfileUrl } from "../../../utils/urlUtils";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Clock,
  Search,
  UserCheck,
  Filter,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import Pagination from "../../Common/Pagination";

export default function ClassTransition() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [academicYears, setAcademicYears] = useState([]);
  const [targetAcademicYear, setTargetAcademicYear] = useState("");
  const [filteredAcademicYear, setFilteredAcademicYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("all");
  const [studentTransitions, setStudentTransitions] = useState({});
  const [classProgressionMap, setClassProgressionMap] = useState({});
  const [promotedStudents, setPromotedStudents] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [studentResults, setStudentResults] = useState({});
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortColumn, setSortColumn] = useState("name");

  // Add pagination state variables after the other state declarations (around line 30)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchAcademicYears();
    fetchClasses();
  }, []);

  useEffect(() => {
    if (filteredAcademicYear) {
      fetchStudents();
      // Set default target academic year as the next year
      const currentYearIndex = academicYears.findIndex(
        (year) => year.value === filteredAcademicYear
      );
      if (currentYearIndex > 0) {
        // academicYears is sorted descending
        setTargetAcademicYear(academicYears[currentYearIndex - 1].value);
      }
    }
  }, [selectedClass, filteredAcademicYear]);

  useEffect(() => {
    if (allClasses.length > 0) {
      createClassProgressionMap();
    }
  }, [allClasses]);

  useEffect(() => {
    if (students.length > 0) {
      // Set default result to "Pass" for all students
      setStudentResults((prev) => {
        const newResults = { ...prev };
        students.forEach((student) => {
          if (!newResults[student._id]) {
            newResults[student._id] = "Pass";
          }
        });
        return newResults;
      });
    }
  }, [students]);

  // Add useEffect to reset to page 1 when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedClass, filteredAcademicYear]);

  const handleStudentClick = (student) => {
    navigate(getStudentProfileUrl(student));
  };

  // Add this function before the getSortedStudents function (around line 200)
  const paginateData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getSortedStudents = (students) => {
    return [...students].sort((a, b) => {
      switch (sortColumn) {
        case "name":
          const nameA = getFullName(a).toLowerCase();
          const nameB = getFullName(b).toLowerCase();
          return sortDirection === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        case "admissionNo":
          const admA = a.admissionNumber || "";
          const admB = b.admissionNumber || "";
          return sortDirection === "asc"
            ? admA.localeCompare(admB)
            : admB.localeCompare(admA);
        case "class":
          const classA = a.classMapping?.class?.name || "";
          const classB = b.classMapping?.class?.name || "";
          return sortDirection === "asc"
            ? classA.localeCompare(classB)
            : classB.localeCompare(classA);
        default:
          return 0;
      }
    });
  };

  const createClassProgressionMap = () => {
    const sortedClasses = [...allClasses].sort((a, b) => {
      // Extract numbers from class names if possible
      const numA = Number.parseInt(a.name.replace(/\D/g, "")) || 0;
      const numB = Number.parseInt(b.name.replace(/\D/g, "")) || 0;
      return numA - numB;
    });

    // Create a map from current class to next class
    const progressionMap = {};
    for (let i = 0; i < sortedClasses.length - 1; i++) {
      progressionMap[sortedClasses[i]._id] = sortedClasses[i + 1]._id;
    }

    // The last class has no progression (can be customized)
    if (sortedClasses.length > 0) {
      progressionMap[sortedClasses[sortedClasses.length - 1]._id] = "";
    }

    setClassProgressionMap(progressionMap);
  };

  const fetchAcademicYears = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        API_ENDPOINTS.academicYears,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        const years = response.data.data.academicYears;

        const formattedYears = years.map((year) => ({
          value: year._id, // Store the ID instead of formatted string
          label: `${year.startYear}-${year.endYear}`, // Keep formatted string for display
          isCurrent: year.isCurrent,
        }));

        formattedYears.sort((a, b) => {
          const [aStart] = a.label.split("-");
          const [bStart] = b.label.split("-");
          return Number.parseInt(bStart) - Number.parseInt(aStart);
        });

        setAcademicYears(formattedYears);

        const currentYear = formattedYears.find((year) => year.isCurrent);
        if (currentYear) {
          setFilteredAcademicYear(currentYear.value);
        } else if (formattedYears.length > 0) {
          setFilteredAcademicYear(formattedYears[0].value);
        }
      }
      setLoading(false);
    } catch (error) {
      setError(
        "Failed to fetch academic years: " +
        (error.response?.data?.message || error.message)
      );
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.classes, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        setAllClasses(response.data.data.classes);
      }
    } catch (error) {
      setError(
        "Failed to fetch classes: " +
        (error.response?.data?.message || error.message)
      );
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setStudentTransitions({});
      const token = localStorage.getItem("token");

      if (!filteredAcademicYear) {
        setStudents([]);
        setLoading(false);
        return;
      }

      let url = API_ENDPOINTS.usersStudents;
      const params = new URLSearchParams();

      if (selectedClass && selectedClass !== "all") {
        params.append("classMapping.class", selectedClass);
      }

      params.append("academicYear", filteredAcademicYear);

      url += `?${params.toString()}`;

      const response = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        let studentsWithClass = response.data.data.students;

        // Additional client-side filtering for class
        if (selectedClass && selectedClass !== "all") {
          studentsWithClass = studentsWithClass.filter(
            (student) => student.classMapping?.class?._id === selectedClass
          );
        }

        const sortedStudents = studentsWithClass.sort((a, b) => {
          const classA = a.classMapping?.class?.name || "Not Assigned";
          const classB = b.classMapping?.class?.name || "Not Assigned";

          if (classA === "Not Assigned" && classB !== "Not Assigned") return 1;
          if (classA !== "Not Assigned" && classB === "Not Assigned") return -1;
          if (classA !== classB) return classA.localeCompare(classB);

          return (a.firstName + " " + a.lastName).localeCompare(
            b.firstName + " " + b.lastName
          );
        });

        setStudents(sortedStudents);

        // Set student results based on data from backend
        const updatedResults = {};
        studentsWithClass.forEach((student) => {
          updatedResults[student._id] = student.result || "Pass";
        });
        setStudentResults(updatedResults);

        if (academicYears.length > 0) {
          try {
            const otherYears = academicYears
              .filter((year) => year.value !== filteredAcademicYear)
              .map((year) => year.value);

            const updatedPromotedStudents = {};

            for (const year of otherYears) {
              const yearResponse = await axiosInstance.get(
                API_ENDPOINTS.usersStudentsByYear(year),
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );

              if (yearResponse.data.status === "success") {
                (yearResponse.data.data.students || []).forEach((student) => {
                  const hasClass = !!(student.classMapping?.class?._id || student.classMapping?.class);
                  if (!hasClass) return;
                  if (!updatedPromotedStudents[student._id]) {
                    updatedPromotedStudents[student._id] = {};
                  }
                  updatedPromotedStudents[student._id][year] = true;
                });
              }
            }

            setPromotedStudents(updatedPromotedStudents);
          } catch (err) {
            console.error("Error fetching promoted students:", err);
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error in fetchStudents:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError(
        "Failed to fetch students: " +
        (error.response?.data?.message || error.message)
      );
      setLoading(false);
    }
  };

  const handlePromoteAllStudents = async () => {
    if (!targetAcademicYear) return;
    // Validate target academic year exists
    if (!academicYears.find((y) => y.value === targetAcademicYear)) {
      setError("Selected target academic year is invalid");
      return;
    }
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const token = localStorage.getItem("token");
      const allStudentIds = students
        .filter(
          (student) =>
            !promotedStudents[student._id]?.[targetAcademicYear] &&
            student.classMapping?.class?._id
        )
        .map((student) => student._id);

      if (allStudentIds.length === 0) {
        setError("No students available for promotion");
        setLoading(false);
        return;
      }

      // Create transitions object based on results
      const transitions = {};
      allStudentIds.forEach((studentId) => {
        const student = students.find((s) => s._id === studentId);
        if (student && student.classMapping?.class?._id) {
          const currentClassId = student.classMapping.class._id;
          const result = studentResults[studentId] || "Pass";

          // For failed students, keep them in the same class
          const nextClassId =
            result === "Fail"
              ? currentClassId
              : classProgressionMap[currentClassId];

          if (nextClassId) {
            transitions[studentId] = nextClassId;
          }
        }
      });

      if (Object.keys(transitions).length === 0) {
        setError("No valid transitions available");
        setLoading(false);
        return;
      }

      const response = await axiosInstance.post(
        API_ENDPOINTS.userStudentsTransition,
        {
          transitions,
          academicYear: targetAcademicYear,
          studentResults, // Include the results in the request
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        setSuccessMessage(
          `Successfully promoted ${Object.keys(transitions).length} students`
        );
        // Update promotedStudents state
        const updatedPromotedStudents = { ...promotedStudents };
        Object.keys(transitions).forEach((studentId) => {
          if (!updatedPromotedStudents[studentId]) {
            updatedPromotedStudents[studentId] = {};
          }
          updatedPromotedStudents[studentId][targetAcademicYear] = true;
        });
        setPromotedStudents(updatedPromotedStudents);
        // Refresh student list to show updated status
        fetchStudents();
      }
    } catch (error) {
      setError(
        "Failed to promote students: " +
        (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      if (Object.keys(studentTransitions).length === 0) {
        setError("No students selected for transition");
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");

      // Validate target academic year exists before submitting
      if (!academicYears.find((y) => y.value === targetAcademicYear)) {
        setError("Selected target academic year is invalid");
        setLoading(false);
        return;
      }

      const response = await axiosInstance.post(
        API_ENDPOINTS.userStudentsTransition,
        {
          transitions: studentTransitions,
          academicYear: targetAcademicYear,
          studentResults: studentResults,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        setSuccessMessage("Class transitions updated successfully");

        if (response.data.data.errors && response.data.data.errors.length > 0) {
          setError(
            `Some transitions failed: ${response.data.data.errors
              .map((e) => e.error)
              .join(", ")}`
          );
        }

        setStudentTransitions({});
        fetchStudents();
      }
    } catch (error) {
      console.error(" Error submitting transitions:", error);
      setError(
        "Failed to update transitions: " +
        (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length >= 2) {
      const suggestions = students
        .filter(
          (student) =>
            student.firstName.toLowerCase().includes(query.toLowerCase()) ||
            student.lastName.toLowerCase().includes(query.toLowerCase()) ||
            (student.admissionNumber &&
              student.admissionNumber.toString().includes(query))
        )
        .slice(0, 5); // Limit to 5 suggestions
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (student) => {
    setSearchQuery(`${student.firstName} ${student.lastName}`);
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  const filteredStudents = students.filter(
    (student) =>
      student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (student.admissionNumber &&
        student.admissionNumber.toString().includes(searchQuery))
  );

  const handleResultChange = (studentId, result) => {
    setStudentResults((prev) => ({
      ...prev,
      [studentId]: result,
    }));
  };

  const handlePromote = async (studentId) => {
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const token = localStorage.getItem("token");

      // Get the student's current class
      const student = students.find((s) => s._id === studentId);

      if (!student || !student.classMapping?.class?._id) {
        console.warn(" Student has no assigned class", studentId);
        setError("Student has no assigned class");
        setLoading(false);
        return;
      }

      const currentClassId = student.classMapping.class._id;
      const studentResult = studentResults[studentId] || "Pass";

      // If student passed, get next class, if failed keep same class
      const nextClassId =
        studentResult === "Pass"
          ? classProgressionMap[currentClassId] // Next class for passed students
          : currentClassId; // Same class for failed students

      if (!nextClassId) {
        console.warn(" No next class defined for student", studentId);
        setError("No next class defined for this student");
        setLoading(false);
        return;
      }

      const transitions = {
        [studentId]: nextClassId,
      };

      const payload = {
        transitions,
        academicYear: targetAcademicYear,
        studentResults: {
          [studentId]: studentResult,
        },
      };

      if (!academicYears.find((y) => y.value === targetAcademicYear)) {
        setError("Selected target academic year is invalid");
        setLoading(false);
        return;
      }

      const response = await axiosInstance.post(
        API_ENDPOINTS.userStudentsTransition,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        setSuccessMessage("Student promoted successfully!");

        // Update promoted students state
        setPromotedStudents((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            [targetAcademicYear]: true,
          },
        }));

        // Refresh the student list to reflect changes
        fetchStudents();
      } else {
        setError(response.data.message || "Failed to promote student");
      }
    } catch (error) {
      console.error(" Error promoting student:", error);
      setError(
        error.response?.data?.message ||
        "An error occurred while promoting student"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const getSortIcon = (column) => {
    if (column !== sortColumn) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    }

    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
    );
  };

  const isPromoted = (studentId) => {
    return promotedStudents[studentId]?.[targetAcademicYear] === true;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pass":
        return (
          <Badge
            variant="outline"
            className="bg-green-100 text-green-800 hover:bg-green-100"
          >
            Pass
          </Badge>
        );
      case "Fail":
        return (
          <Badge
            variant="outline"
            className="bg-red-100 text-red-800 hover:bg-red-100"
          >
            Fail
          </Badge>
        );
      default:
        return (
          <Badge
            variant="outline"
            className="bg-gray-100 text-gray-800 hover:bg-gray-100"
          >
            {status}
          </Badge>
        );
    }
  };

  const hasAssignedClass = (student) =>
    !!(student?.classMapping?.class?._id || student?.classMapping?.class);

  const getPromotionStatus = (student) => {
    if (!student) return null;
    const assigned = hasAssignedClass(student);
    if (!assigned) {
      return (
        <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
          Unassigned
        </Badge>
      );
    }
    if (isPromoted(student._id)) {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200">
          Promoted
        </Badge>
      );
    }
    return null;
  };

  const eligibleForPromotionCount = students.filter(
    (s) => hasAssignedClass(s) && !promotedStudents[s._id]?.[targetAcademicYear]
  ).length;
  const filteredEligibleCount = filteredStudents.filter(
    (s) => hasAssignedClass(s) && !promotedStudents[s._id]?.[targetAcademicYear]
  ).length;
  const filteredPromotedCount = filteredStudents.filter(
    (s) => hasAssignedClass(s) && promotedStudents[s._id]?.[targetAcademicYear]
  ).length;
  const filteredUnassignedCount = filteredStudents.filter((s) => !hasAssignedClass(s)).length;

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-[#0263cc] text-xl font-semibold">
                Class Transition
              </CardTitle>
              <CardDescription>
                Promote students to the next academic year. Assign class/section first for unassigned students.
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative w-full sm:w-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                />
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {searchSuggestions.map((student) => (
                      <div
                        key={student._id}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleSuggestionClick(student)}
                      >
                        {student.firstName} {student.lastName} -{" "}
                        {student.admissionNumber || "N/A"}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 p-0 border-[#0263cc]/20"
                  >
                    <Filter className="h-4 w-4" />
                    <span className="sr-only">Filter</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px]">
                  <DropdownMenuRadioGroup
                    value={`${sortColumn}-${sortDirection}`}
                    onValueChange={(value) => {
                      const [column, direction] = value.split("-");
                      setSortColumn(column);
                      setSortDirection(direction);
                    }}
                  >
                    <DropdownMenuRadioItem value="name-asc">
                      Name (A to Z)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="name-desc">
                      Name (Z to A)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="admissionNo-asc">
                      Admission No. (Ascending)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="admissionNo-desc">
                      Admission No. (Descending)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="class-asc">
                      Class (Ascending)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="class-desc">
                      Class (Descending)
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="p-4 bg-white border-b border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label
                  htmlFor="current-year"
                  className="text-sm font-medium text-gray-600 mb-1.5 block"
                >
                  From (Academic Year)
                </Label>
                <Select
                  value={filteredAcademicYear}
                  onValueChange={setFilteredAcademicYear}
                >
                  <SelectTrigger
                    id="current-year"
                    className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg"
                  >
                    <SelectValue placeholder="Select Academic Year" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                    {academicYears.map((year) => (
                      <SelectItem
                        key={year.value}
                        value={year.value}
                        className="cursor-pointer"
                      >
                        {year.label} {year.isCurrent ? "(Current)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="target-year"
                  className="text-sm font-medium text-gray-600 mb-1.5 block"
                >
                  Promote to (Target Year)
                </Label>
                <Select
                  value={targetAcademicYear}
                  onValueChange={setTargetAcademicYear}
                >
                  <SelectTrigger
                    id="target-year"
                    className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg"
                  >
                    <SelectValue placeholder="Select target year" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                    {academicYears.filter((y) => y.value !== filteredAcademicYear).length === 0 ? (
                      <div className="py-4 px-3 text-sm text-gray-500 text-center">
                        No other academic year
                      </div>
                    ) : (
                      academicYears
                        .filter((y) => y.value !== filteredAcademicYear)
                        .map((year) => (
                          <SelectItem
                            key={year.value}
                            value={year.value}
                            className="cursor-pointer"
                          >
                            {year.label}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="class-filter"
                  className="text-sm font-medium text-gray-600 mb-1.5 block"
                >
                  Filter by Class
                </Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger
                    id="class-filter"
                    className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg"
                  >
                    <SelectValue placeholder="Filter by Class" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                    <SelectItem value="all" className="cursor-pointer">
                      All Classes
                    </SelectItem>
                    {allClasses.map((cls) => (
                      <SelectItem
                        key={cls._id}
                        value={cls._id}
                        className="cursor-pointer"
                      >
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {(error || successMessage) && (
            <Alert
              variant={error ? "destructive" : "default"}
              className={`m-4 rounded-lg ${successMessage
                  ? "bg-green-50 text-green-800 border-green-200"
                  : error
                    ? "bg-red-50 text-red-800 border-red-200"
                    : ""
                }`}
            >
              <AlertDescription className="font-medium">
                {error || successMessage}
              </AlertDescription>
            </Alert>
          )}

          {loading && !students.length ? (
            <div className="flex justify-center items-center p-8">
              <div className="flex items-center space-x-2">
                <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
                <span className="text-gray-600">Loading data...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-gray-600">
                    <span className="font-medium text-gray-800">{filteredStudents.length}</span> students
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-sm">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-normal">
                      {filteredPromotedCount} promoted
                    </Badge>
                  </span>
                  <span className="text-sm">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-normal">
                      {filteredEligibleCount} eligible
                    </Badge>
                  </span>
                  {filteredUnassignedCount > 0 && (
                    <span className="text-sm">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-normal">
                        {filteredUnassignedCount} unassigned
                      </Badge>
                    </span>
                  )}
                </div>
                <Button
                  onClick={handlePromoteAllStudents}
                  disabled={
                    !targetAcademicYear ||
                    eligibleForPromotionCount === 0 ||
                    loading
                  }
                  className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white shrink-0"
                >
                  {loading ? (
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <UserCheck className="mr-2 h-4 w-4" />
                  )}
                  Promote All ({eligibleForPromotionCount})
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200">
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleTeacherSort("name")}
                      >
                        <div className="flex items-center">
                          Student Name
                          {getSortIcon("name")}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleTeacherSort("admissionNo")}
                      >
                        <div className="flex items-center">
                          Admission No.
                          {getSortIcon("admissionNo")}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Current Academic Year
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleTeacherSort("class")}
                      >
                        <div className="flex items-center">
                          Current Class
                          {getSortIcon("class")}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Section
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Result
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredStudents.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          {searchQuery
                            ? "No students found matching your search"
                            : "No students available"}
                        </td>
                      </tr>
                    ) : (
                      paginateData(getSortedStudents(filteredStudents)).map(
                        (student) => {
                          const currentClassId =
                            student.classMapping?.class?._id;

                          return (
                            <tr
                              key={student._id}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-4">
                                <span
                                  className="text-[#0263cc] hover:text-[#0263cc]/80 cursor-pointer font-medium"
                                  onClick={() => handleStudentClick(student)}
                                >
                                  {getFullName(student)}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                {student.admissionNumber || "N/A"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                {student.classMapping?.academicYear
                                  ? `${student.classMapping.academicYear.startYear}-${student.classMapping.academicYear.endYear}`
                                  : "Not Assigned"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                {student.classMapping?.class?.name ||
                                  "Not Assigned"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                {student.classMapping?.section?.name ||
                                  "Not Assigned"}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center space-x-2">
                                  <Select
                                    value={
                                      studentResults[student._id] || "Pass"
                                    }
                                    onValueChange={(value) =>
                                      handleResultChange(student._id, value)
                                    }
                                    disabled={!hasAssignedClass(student) || isPromoted(student._id)}
                                  >
                                    <SelectTrigger className="w-24 h-8 border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                                      <SelectValue placeholder="Result" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                                      <SelectItem
                                        value="Pass"
                                        className="cursor-pointer"
                                      >
                                        Pass
                                      </SelectItem>
                                      <SelectItem
                                        value="Fail"
                                        className="cursor-pointer"
                                      >
                                        Fail
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {!isPromoted(student._id) &&
                                    getStatusBadge(
                                      studentResults[student._id] || "Pass"
                                    )}
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center space-x-2">
                                  {getPromotionStatus(student)}
                                  {hasAssignedClass(student) && !isPromoted(student._id) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handlePromote(student._id)}
                                      disabled={
                                        isPromoted(student._id) ||
                                        loading ||
                                        (studentResults[student._id] ===
                                          "Fail" &&
                                          !currentClassId)
                                      }
                                      className="h-8 border-[#0263cc]/20 hover:bg-[#0263cc]/5 hover:text-[#0263cc] hover:border-[#0263cc]"
                                    >
                                      <UserCheck className="mr-1 h-3.5 w-3.5" />
                                      Promote
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {Object.keys(studentTransitions).length > 0 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
                  >
                    {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
        <Pagination
          currentPage={currentPage}
          totalItems={filteredStudents.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          itemLabel="students"
        />
      </Card>
    </div>
  );
}
